import { CHUNK_SIZE, type ChunkData, type Instance } from './chunk';
import { SCAR_CELL } from './overlay';
import type { SereSnapshot } from '@/sim/persistence/snapshot';

/**
 * The sere: what ground remembers about its last burn, for decades — the
 * memory the Overlay deliberately does not keep. `applyOverlay`'s scar is
 * the CHAR, a colour that heals in forty days; this is the STRUCTURE, the
 * age of the stand growing back, and it runs on a century clock.
 *
 * Succession (Design III §52) is a trajectory away from a disturbance, and
 * every constant here was measured before it was picked (`tools/sere.ts`,
 * 150 years of the natural regime): reburns are rare (7.6% of burned
 * ground) and slow (p50 gap 43.8 y, none while still grass), so ONE field
 * per cell — when it last burned — is the whole state, and stage is a pure
 * function of (age). At ~0.6 km²/yr of new burns and retirement at eighty
 * years, the store's equilibrium is ~50k cells: sparse, bounded, and
 * self-erasing — recovered ground rejoins the pure baseline and its cell is
 * DELETED, which is CURRENT WORLD = BASELINE + SPARSE HISTORY on the
 * longest clock the world has yet kept.
 *
 * Law 8 (no-resize) shapes the renderer half: `applySere` only OMITS
 * baseline vegetation — a thirty-year stand is the baseline stand minus
 * everything too old to have regrown, never a rescaled copy — so every LOD
 * agrees about every plant, and a chunk rebuilt with an empty store is
 * byte-identical to the baseline.
 */

/** The recovery ladder. Ages in years since the burn. */
export type SereStage = 'bare' | 'grass' | 'shrub' | 'young';

const YEAR = 365 * 86400;
/** Stage boundaries in years: bare→grass→shrub→young→(retired = climax). */
export const STAGE_AGES = { grass: 1, shrub: 6, young: 25, retire: 80 } as const;

/**
 * What each stage offers a fire, as a multiplier on baseline fuel — §60's
 * "fuel accumulates because a place has not burned" made literal. Bare
 * ground carries nothing; grass is thin; shrub is the flammable middle age
 * (real chaparral's dangerous decades); a young stand is greener than its
 * climax. Climax — no cell — is 1, the baseline.
 */
export const STAGE_FUEL: Record<SereStage, number> = {
  bare: 0.05, grass: 0.5, shrub: 0.9, young: 0.75,
};

/**
 * The tallest thing that could have regrown by each stage, metres — the
 * omission rule. Grass keeps only the ground layer, shrub keeps shrubs, a
 * young stand keeps everything but the emergent giants a century makes.
 */
const STAGE_CEILING: Record<SereStage, number> = {
  bare: 0, grass: 1.2, shrub: 3.0, young: 14,
};

export const stageOfAge = (ageYears: number): SereStage | null => {
  if (ageYears < 0) return null;
  if (ageYears < STAGE_AGES.grass) return 'bare';
  if (ageYears < STAGE_AGES.shrub) return 'grass';
  if (ageYears < STAGE_AGES.young) return 'shrub';
  if (ageYears < STAGE_AGES.retire) return 'young';
  return null;   // recovered: climax again
};

interface SereCell { gx: number; gz: number; at: number }

const CELLS_PER_CHUNK = CHUNK_SIZE / SCAR_CELL;
const cellChunk = (gx: number, gz: number) =>
  `${Math.floor(gx / CELLS_PER_CHUNK)},${Math.floor(gz / CELLS_PER_CHUNK)}`;

/**
 * How much of a coarse cell stands at each recovery stage — fractions of the
 * cell's AREA, summing to at most 1 (the rest is climax). What a population
 * model reads: a cell that is a third grass and a tenth shrub is a different
 * range from one that is all climax wood, and until this existed nothing
 * outside the renderer could tell (`tools/trajectory-drift.ts`: a burned
 * social cell's herds moved exactly as an unburned one's did).
 */
export type StageShares = Record<SereStage, number>;

export class SereStore {
  private readonly cells = new Map<string, SereCell>();
  private version_ = 0;
  /** `stageShares` cache — one pass over the store per (day, version, grid). */
  private sharesKey = '';
  private shares = new Map<string, StageShares>();

  /** A burn happened here: the recovery clock (re)starts at `at`. */
  recordBurn(wx: number, wz: number, at: number): void {
    const gx = Math.floor(wx / SCAR_CELL), gz = Math.floor(wz / SCAR_CELL);
    const key = `${gx},${gz}`;
    const prev = this.cells.get(key);
    // a reburn restarts the clock; time never runs backwards through it
    if (!prev || at > prev.at) {
      this.cells.set(key, { gx, gz, at });
      this.version_++;
    }
  }

  /** The recovery stage under a world position, or null at climax. */
  stageAt(wx: number, wz: number, now: number): SereStage | null {
    const c = this.cells.get(
      `${Math.floor(wx / SCAR_CELL)},${Math.floor(wz / SCAR_CELL)}`);
    if (!c) return null;
    return stageOfAge((now - c.at) / YEAR);
  }

  /** Years since the last burn here, or null at climax. */
  ageAt(wx: number, wz: number, now: number): number | null {
    const c = this.cells.get(
      `${Math.floor(wx / SCAR_CELL)},${Math.floor(wz / SCAR_CELL)}`);
    if (!c) return null;
    const age = (now - c.at) / YEAR;
    return age >= STAGE_AGES.retire ? null : age;
  }

  /** Baseline-fuel multiplier here — 1 at climax. What the fire reads. */
  fuelFactorAt(wx: number, wz: number, now: number): number {
    const s = this.stageAt(wx, wz, now);
    return s ? STAGE_FUEL[s] : 1;
  }

  /**
   * The day's bookkeeping: delete cells that have recovered, and report the
   * chunks whose look changed because a cell crossed a stage boundary since
   * `now - dt`. Boundaries are pure functions of `at`, so a crossing is
   * detected exactly and exactly once, whatever the call pattern.
   */
  sweep(now: number, dt = 86400): string[] {
    const dirty = new Set<string>();
    for (const [key, c] of this.cells) {
      const before = stageOfAge((now - dt - c.at) / YEAR);
      const after = stageOfAge((now - c.at) / YEAR);
      // a cell dated after `now` is not recovered, it is not yet burned as far
      // as this sweep can see — leave it (a probe that pre-records a burn and
      // sweeps up to it must not have the store forget the burn)
      if (after === null && now >= c.at) {
        this.cells.delete(key);
        dirty.add(cellChunk(c.gx, c.gz));
      } else if (after !== before) {
        dirty.add(cellChunk(c.gx, c.gz));
      }
    }
    if (dirty.size > 0) this.version_++;
    return [...dirty];
  }

  /** All chunk keys currently carrying recovering ground. */
  chunks(): string[] {
    const keys = new Set<string>();
    for (const c of this.cells.values()) keys.add(cellChunk(c.gx, c.gz));
    return [...keys];
  }

  /**
   * The island-wide recovery ceiling, point-sampled onto an n×n grid of
   * `cellMetres` cells centred on the origin — the far layers' read of this
   * store. One byte per cell: the tallest thing that could stand there in
   * metres, 255 where the ground is climax and nothing is owed.
   *
   * POINT-sampled at each grid cell's centre, never aggregated: the census
   * (`tools/sere.ts`) licensed 260 m sampling exactly because burned AREA
   * rides patches that survive that resolution — a lone 32 m cell should
   * vanish here, not annex its whole texel. Iterates the sparse store and
   * touches only cells that contain a grid centre, so cost follows burns.
   */
  ceilingField(now: number, n: number, cellMetres: number, out: Uint8Array): void {
    out.fill(255);
    const half = (n * cellMetres) / 2;
    for (const c of this.cells.values()) {
      const stage = stageOfAge((now - c.at) / YEAR);
      if (stage === null) continue;
      // the one grid index whose centre could lie inside this 32 m cell
      const i = Math.round((c.gx * SCAR_CELL + SCAR_CELL / 2 + half) / cellMetres - 0.5);
      const j = Math.round((c.gz * SCAR_CELL + SCAR_CELL / 2 + half) / cellMetres - 0.5);
      if (i < 0 || i >= n || j < 0 || j >= n) continue;
      const cx = -half + (i + 0.5) * cellMetres;
      const cz = -half + (j + 0.5) * cellMetres;
      if (cx < c.gx * SCAR_CELL || cx >= (c.gx + 1) * SCAR_CELL) continue;
      if (cz < c.gz * SCAR_CELL || cz >= (c.gz + 1) * SCAR_CELL) continue;
      out[j * n + i] = Math.min(254, Math.ceil(STAGE_CEILING[stage]));
    }
  }

  /**
   * Stage shares for the `cellMetres`-square cell containing (wx, wz) —
   * area fractions at bare / grass / shrub / young, the remainder climax —
   * as of the world-day containing `now`.
   *
   * Aggregated, unlike `ceilingField`, and on purpose: the far canopy asks
   * "what stands at this point", a herd asks "how much of my range is grass".
   * The whole store is walked ONCE per (day, version, grid) and binned, then
   * every cell reads its bin, so the cost of asking follows the burns rather
   * than the askers. Readings are canonical per world-day so a value cannot
   * depend on when in the day it was asked for (the habitat sampler's rule).
   */
  stageSharesAt(wx: number, wz: number, cellMetres: number, now: number): StageShares {
    const day = Math.floor(now / 86400);
    const key = `${day}|${this.version_}|${cellMetres}`;
    if (key !== this.sharesKey) {
      this.sharesKey = key;
      this.shares = new Map();
      const per = SCAR_CELL / cellMetres;
      const w = per * per;   // one 32 m cell's share of the coarse cell's area
      const t = day * 86400;
      for (const c of this.cells.values()) {
        const stage = stageOfAge((t - c.at) / YEAR);
        if (stage === null) continue;
        const k = `${Math.floor(c.gx * per)},${Math.floor(c.gz * per)}`;
        let s = this.shares.get(k);
        if (!s) { s = { bare: 0, grass: 0, shrub: 0, young: 0 }; this.shares.set(k, s); }
        s[stage] += w;
      }
    }
    const s = this.shares.get(`${Math.floor(wx / cellMetres)},${Math.floor(wz / cellMetres)}`);
    // a copy, never the cached record — a caller scribbling on its reading
    // must not poison every later read of the same day
    return s ? { ...s } : { bare: 0, grass: 0, shrub: 0, young: 0 };
  }

  get size(): number { return this.cells.size; }

  /** Cache tag, not history: bumps whenever the recovering set changes. */
  get version(): number { return this.version_; }

  toSnapshot(): SereSnapshot {
    return { cells: [...this.cells.values()].map((c) => ({ ...c })) };
  }

  /** Rebuild from a snapshot. Shares nothing with the source. */
  static fromSnapshot(snap: SereSnapshot): SereStore {
    const store = new SereStore();
    for (const c of snap.cells) store.cells.set(`${c.gx},${c.gz}`, { ...c });
    return store;
  }
}

/**
 * Stamp recovery onto freshly built chunk data, on the main thread — the
 * fifth transform, beside `applyEnv`/`applyWear`/`applyOverlay`/
 * `applyThings`. Omission ONLY: a stand growing back is the baseline stand
 * minus what is too old to be there yet. The ground's colour is untouched —
 * fresh char belongs to the Overlay, and any stage tinting is the
 * renderer's decision to make later, from the same `stageAt` field.
 *
 * `spare` is the one thread promotion runs back down the chain (§4): the
 * tree the fire went round still stands in the bare year, the only living
 * thing on the burn — which is exactly the kind of thing a mortal who sees
 * it will need to explain.
 */
export function applySere(
  data: ChunkData,
  sere: SereStore,
  now: number,
  spare?: (x: number, z: number) => boolean,
): ChunkData {
  if (sere.size === 0) return data;
  const kept: Instance[] = [];
  for (const inst of data.vegetation) {
    const stage = sere.stageAt(inst.x, inst.z, now);
    if (stage === null || inst.scaleY <= STAGE_CEILING[stage] ||
        spare?.(inst.x, inst.z)) {
      kept.push(inst);
    }
  }
  data.vegetation = kept;
  return data;
}
